import { axiosInstance } from "../api/axiosInstance";
import { Constants } from "../modules/common/shared/Constants";
import { getRefreshToken, saveAccessToken } from "../modules/common/user/UserService";

/** No click / key press for this long ends the session (even if the token is still valid). */
export const IDLE_LIMIT_MS = 45 * 60 * 1000;
/** The access token is renewed this long before it expires. */
export const REFRESH_AHEAD_MS = 2 * 60 * 1000;

const ACTIVITY_WRITE_EVERY_MS = 30 * 1000;
let lastWrite = 0;

/** Records user activity (throttled; localStorage so every tab sees it). */
export const recordActivity = () => {
  const now = Date.now();
  if (now - lastWrite < ACTIVITY_WRITE_EVERY_MS) return;
  lastWrite = now;
  try {
    localStorage.setItem(Constants.LAST_ACTIVITY_AT, String(now));
  } catch {
    // storage blocked: idle time then counts from login
  }
};

/** Last activity in epoch millis (login time if none recorded). */
export const lastActivityAt = (): number => {
  try {
    const value = Number(localStorage.getItem(Constants.LAST_ACTIVITY_AT));
    return Number.isFinite(value) && value > 0 ? value : Date.now();
  } catch {
    return Date.now();
  }
};

export const isIdle = () => Date.now() - lastActivityAt() >= IDLE_LIMIT_MS;

let inFlight: Promise<boolean> | null = null;

/**
 * Gets a new access token with the refresh token from login (POST /auth/refresh). Concurrent callers
 * share one request. Resolves false when there is no refresh token or it is no longer accepted
 * (expired after 1 day, user disabled) - the session then ends when the access token expires.
 */
export const refreshAccessToken = (): Promise<boolean> => {
  if (inFlight) return inFlight;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(false);
  inFlight = axiosInstance
    .post<{ token: string }>("/auth/refresh", { refreshToken })
    .then((response) => {
      if (!response.data?.token) return false;
      saveAccessToken(response.data.token);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};
